import React, { useState, useEffect, useRef } from 'react';
import './Navbar.scss';
import { MenuItem, MenuId } from '../../types/Header/types';
import { 
  HomeOutlined,
  AppstoreOutlined,
  CodeOutlined,
  LaptopOutlined,
  CloudOutlined,
  BankOutlined,
  ApiOutlined,
  ToolOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  ContactsOutlined,
  UserOutlined,
  LoginOutlined
} from '@ant-design/icons';

const Navigation: React.FC = () => {
  const [activeMenu, setActiveMenu] = useState<MenuId | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 完整的导航数据
  const navItems: MenuItem[] = [
    {
      id: 'home',
      title: '首页',
      link: '/',
      icon: 'home'
    },
    {
      id: 'products',
      title: '产品中心',
      icon: 'appstore',
      subMenu: [
        {
          id: 'software',
          title: '软件产品',
          icon: 'code',
          subMenu: [
            { id: 'software01', title: '云投屏软件', link: '/products/software/dlna', icon: 'office' },
            { id: 'software02', title: '云E助手', link: '/products/software/02', icon: 'design', hot: false },
            { id: 'software03', title: '云短音通信软件', link: '/products/software/03', icon: 'tool', new: false }
          ]
        },
        // {
        //   id: 'hardware',
        //   title: '硬件产品',
        //   icon: 'laptop',
        //   subMenu: [
        //     { id: 'pc', title: '工作站电脑', link: '/products/hardware/pc', icon: 'desktop' },
        //     { id: 'server', title: '服务器设备', link: '/products/hardware/server', icon: 'server', hot: true },
        //     { id: 'network', title: '网络设备', link: '/products/hardware/network', icon: 'network' }
        //   ]
        // },
        {
          id: 'cloud',
          title: '云服务',
          icon: 'cloud',
          subMenu: [
            { id: 'storage', title: '云存储服务', link: '/products/cloud/storage', icon: 'storage' },
            { id: 'compute', title: '云计算平台', link: '/products/cloud/compute', icon: 'compute' },
            { id: 'ai', title: 'AI云服务', link: '/products/cloud/ai', icon: 'ai', new: false }
          ]
        }
      ]
    },
    {
      id: 'solutions',
      title: '解决方案',
      icon: 'solution',
      subMenu: [
        {
          id: 'industry',
          title: '行业解决方案',
          icon: 'bank',
          subMenu: [
            { id: 'finance', title: '金融行业解决方案', link: '/solutions/industry/finance', icon: 'finance', hot: false },
            { id: 'education', title: '教育行业解决方案', link: '/solutions/industry/education', icon: 'education' },
            { id: 'healthcare', title: '医疗健康方案', link: '/solutions/industry/healthcare', icon: 'healthcare' }
          ]
        },
        {
          id: 'tech',
          title: '技术解决方案',
          icon: 'api',
          subMenu: [
            { id: 'bigdata', title: '大数据解决方案', link: '/solutions/tech/bigdata', icon: 'data' },
            { id: 'iot', title: '物联网解决方案', link: '/solutions/tech/iot', icon: 'iot' },
            // { id: 'blockchain', title: '区块链解决方案', link: '/solutions/tech/blockchain', icon: 'blockchain', new: true }
          ]
        },
        {
          id: 'custom',
          title: '定制开发',
          icon: 'tool',
          subMenu: [
            { id: 'web', title: '企业网站定制', link: '/solutions/custom/web', icon: 'web' },
            { id: 'app', title: '移动应用开发', link: '/solutions/custom/app', icon: 'mobile', hot: true },
            { id: 'erp', title: 'ERP系统定制', link: '/solutions/custom/erp', icon: 'erp' }
          ]
        }
      ]
    },
    {
      id: 'services',
      title: '服务支持',
      icon: 'service',
      subMenu: [
        {
          id: 'support',
          title: '技术支持',
          icon: 'question',
          subMenu: [
            { id: 'online', title: '在线技术支持', link: '/services/support/online', icon: 'online' },
            { id: 'onsite', title: '现场技术支持', link: '/services/support/onsite', icon: 'onsite' },
            // { id: 'emergency', title: '紧急救援服务', link: '/services/support/emergency', icon: 'emergency', hot: false }
          ]
        },
        {
          id: 'training',
          title: '培训服务',
          icon: 'read',
          subMenu: [
            { id: 'basic', title: '基础培训课程', link: '/services/training/basic', icon: 'basic' },
            { id: 'advanced', title: '高级技术培训', link: '/services/training/advanced', icon: 'advanced' },
            { id: 'certification', title: '认证培训计划', link: '/services/training/certification', icon: 'certification', new: false }
          ]
        },
        {
          id: 'consulting',
          title: '咨询服务',
          icon: 'message',
          subMenu: [
            { id: 'it', title: 'IT战略咨询', link: '/services/consulting/it', icon: 'strategy' },
            { id: 'digital', title: '数字化转型咨询', link: '/services/consulting/digital', icon: 'digital', hot: false },
            { id: 'security', title: '安全合规咨询', link: '/services/consulting/security', icon: 'security' }
          ]
        }
      ]
    },
    {
      id: 'about',
      title: '关于我们',
      icon: 'info',
      subMenu: [
        {
          id: 'company',
          title: '团队简介',
          icon: 'info',
          subMenu: [
            { id: 'profile', title: '团队概况', link: '/about/company/profile', icon: 'profile' },
            { id: 'culture', title: '团队文化', link: '/about/company/culture', icon: 'culture' },
            // { id: 'honors', title: '荣誉资质', link: '/about/company/honors', icon: 'award' }
          ]
        },
        // {
        //   id: 'history',
        //   title: '发展历程',
        //   icon: 'history',
        //   subMenu: [
        //     { id: 'milestones', title: '重要里程碑', link: '/about/history/milestones', icon: 'milestone' },
        //     { id: 'events', title: '大事件记', link: '/about/history/events', icon: 'event', hot: false }
        //   ]
        // },
        {
          id: 'history',
          title: '报告发布',
          icon: 'report',
          subMenu: [
            { id: 'report01', title: '产品报告', link: '/about/report/p01', icon: 'milestone' },
            { id: 'report02', title: '动态消息', link: '/about/report/p02', icon: 'event', hot: false }
          ]
        },
        {
          id: 'contact',
          title: '联系我们',
          icon: 'contact',
          subMenu: [
            // { id: 'locations', title: '全球办事处', link: '/about/contact/locations', icon: 'location' },
            { id: 'joinus', title: '加入我们', link: '/about/contact/joinus', icon: 'join', new: false },
            // { id: 'partner', title: '合作伙伴', link: '/about/contact/partner', icon: 'partner' }
          ]
        }
      ]
    }
  ];

  // 获取图标组件
  const getIcon = (iconName?: string) => {
    switch(iconName) {
      case 'home': return <HomeOutlined />;
      case 'appstore': return <AppstoreOutlined />;
      case 'code': return <CodeOutlined />;
      case 'laptop': return <LaptopOutlined />;
      case 'cloud': return <CloudOutlined />;
      case 'solution': return <BankOutlined />;
      case 'api': return <ApiOutlined />;
      case 'tool': return <ToolOutlined />;
      case 'question': return <QuestionCircleOutlined />;
      case 'read': return <ReadOutlined />;
      case 'message': return <MessageOutlined />;
      case 'info': return <InfoCircleOutlined />;
      case 'history': return <HistoryOutlined />;
      case 'contact': return <ContactsOutlined />;
      default: return <AppstoreOutlined />;
    }
  };

  // 检测屏幕尺寸变化
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 处理菜单悬停
  const handleMouseEnter = (menuId: MenuId) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setActiveMenu(menuId);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 200);
  };

  // 处理移动端菜单点击
  const handleMenuClick = (menuId: MenuId) => {
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  // 渲染菜单项
  const renderMenuItems = (items: MenuItem[], level: number = 1) => {
    return (
      <ul className={`nav-level-${level}`}>
        {items.map((item) => (
          <li 
            key={item.id}
            className={`
              nav-item 
              level-${level}
              ${activeMenu === item.id ? 'active' : ''}
              ${item.hot ? 'hot' : ''}
              ${item.new ? 'new' : ''}
            `}
            onMouseEnter={!isMobile ? () => handleMouseEnter(item.id) : undefined}
            onMouseLeave={!isMobile ? handleMouseLeave : undefined}
          >
            <a 
              href={item.link || '#'}
              onClick={(e) => {
                if (isMobile && item.subMenu) {
                  e.preventDefault();
                  handleMenuClick(item.id);
                }
              }}
            >
              {level > 1 && <span className="menu-icon">{getIcon(item.icon)}</span>}
              {item.title}
              {(item.hot || item.new) && (
                <span className="menu-badge">
                  {item.hot && <span className="hot-badge">热门</span>}
                  {item.new && <span className="new-badge">新品</span>}
                </span>
              )}
            </a>
            
            {item.subMenu && (
              <div 
                className={`sub-menu level-${level + 1} ${
                  activeMenu === item.id || 
                  (level === 1 && item.subMenu.some(sub => sub.id === activeMenu)) ? 'active' : ''
                }`}
                onMouseEnter={!isMobile ? () => handleMouseEnter(item.id) : undefined}
                onMouseLeave={!isMobile ? handleMouseLeave : undefined}
              >
                {renderMenuItems(item.subMenu, level + 1)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div 
      className="navigation-container" 
      ref={navRef}
      onMouseLeave={!isMobile ? () => setActiveMenu(null) : undefined}
    >
      {/* 网站Logo区域 */}
      <div className="logo-area">
        <a href="/" className="site-logo">
          <span className="logo-text">G135</span>
          <span className="logo-subtitle">科技驱动未来</span>
        </a>
      </div>

      {/* 移动端菜单按钮 */}
      {isMobile && (
        <button 
          className="mobile-menu-button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
        >
          <span className="menu-text">
            {menuOpen ? '关闭' : '菜单'}
          </span>
        </button>
      )}
      
      <nav className={`main-navigation ${menuOpen ? 'open' : ''}`}>
        <div className="nav-menu-container">
          {renderMenuItems(navItems)}
        </div>
        
        {/* 登录/注册按钮 */}
        <div className="auth-buttons">
          <a href="https://socks5.g135.cn/login" className="login-btn">
            <LoginOutlined /> 登录
          </a>
          <a href="https://socks5.g135.cn/register" className="register-btn">
            <UserOutlined /> 注册
          </a>
        </div>
      </nav>
    </div>
  );
};

export default Navigation;